import {
  updateLineItemCalculationsById as legacyUpdateLineItemCalculationsById,
  updateLineItemCalculationsUsingNightlyJobLogicById
} from '@client/state/resources/line-items/actions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, useCallback, useRef, useState } from 'react';

type AsyncUpdate = () => Promise<void>;

export const GateUpdateContext = createContext<((update: AsyncUpdate) => Promise<void>) | null>(null);

export const useSaveCoordination = () => {
  const activeSave = useRef<Promise<void> | null>(null);
  const gateUpdateCount = useRef(0);
  const pendingRefresh = useRef<AsyncUpdate | null>(null);
  const [isGateUpdatePending, setIsGateUpdatePending] = useState(false);

  const isSaveBlocked = useCallback(() => gateUpdateCount.current > 0 || activeSave.current !== null, []);

  const refreshAfterSave = useCallback(async (refresh: AsyncUpdate) => {
    if (gateUpdateCount.current > 0) {
      // Do not await Apply here: it must first wait for this Save to settle.
      pendingRefresh.current = refresh;
    } else {
      await refresh();
    }
  }, []);

  const runSave = useCallback(
    async (save: AsyncUpdate) => {
      if (isSaveBlocked()) {
        return;
      }

      const pendingSave = Promise.resolve().then(save);
      activeSave.current = pendingSave;

      try {
        await pendingSave;
      } finally {
        activeSave.current = null;
      }
    },
    [isSaveBlocked]
  );

  const runGateUpdate = useCallback(async (update: AsyncUpdate) => {
    gateUpdateCount.current += 1;
    setIsGateUpdatePending(true);

    try {
      if (activeSave.current) {
        // A failed save may still have persisted some requests; wait for settlement before writing the gate time.
        await Promise.allSettled([activeSave.current]);
      }

      await update();

      if (gateUpdateCount.current === 1 && pendingRefresh.current) {
        const refresh = pendingRefresh.current;
        pendingRefresh.current = null;
        await refresh();
      }
    } catch (err) {
      // Refreshing would unmount failed editors and discard their retryable drafts.
      pendingRefresh.current = null;
      throw err;
    } finally {
      gateUpdateCount.current -= 1;
      setIsGateUpdatePending(gateUpdateCount.current > 0);
    }
  }, []);

  return { isGateUpdatePending, isSaveBlocked, runSave, runGateUpdate, refreshAfterSave };
};

export const useUpdateLineItemCalculationsById = () => {
  const flags = useFlags();

  return flags.useNightlyJobLogicToRecalculateSystemCalculatedLineItems
    ? updateLineItemCalculationsUsingNightlyJobLogicById
    : legacyUpdateLineItemCalculationsById;
};
